/**
 * Areas Catalog Service
 */

import { apiClient } from '@/api/core/apiClient.ts';
import { API_CONFIG } from '@/api/core/apiConfig.ts';
import type { ApiResponse } from '@/api/shared/types.ts';
import { buildQueryString } from '@/api/shared';
import type { CatalogScope } from '@/api/domains/catalog/shared/catalogScope.ts';
import type { LC_GetAreasOut, Area } from './areas.types.ts';
import { mapAreaOutToModel } from './areas.mapper.ts';


export class AreasCatalogService {
  /**
   * Get available areas.
   * By default scoped to the authenticated user's company; pass scope: 'holding'
   * to widen the catalog to every company in the user's holding.
   * Omitting scope preserves the original URL for existing consumers.
   */
  async getAreas(
    options?: { scope?: CatalogScope },
    signal?: AbortSignal,
  ): Promise<ApiResponse<Area[]>> {
    const queryString = options?.scope === 'holding'
      ? buildQueryString({ scope: 'holding' })
      : '';
    const endpoint = `${API_CONFIG.ENDPOINTS.CATALOG.AREAS}${queryString}`;
    // Preserve the original single-argument call when no signal is supplied so existing
    // consumers (and their strict-argument tests) remain unaffected.
    const response = signal
      ? await apiClient.get<LC_GetAreasOut>(endpoint, { signal })
      : await apiClient.get<LC_GetAreasOut>(endpoint);

    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.map(mapAreaOutToModel),
      };
    }

    return { ...response, data: null };
  }
}

export const areasCatalogService = new AreasCatalogService();
